import React, { useState, useEffect } from 'react';
import { Modal, Box, IconButton, Typography } from '@mui/material';
import { ChevronRight, Add, Remove, Search } from '@mui/icons-material';
import DocViewer, { DocViewerRenderers } from '@cyntler/react-doc-viewer';
import { DownloadOutlined } from '@ant-design/icons';
import "@cyntler/react-doc-viewer/dist/index.css";
import { downloadFile } from "../../../features/claimsNew/utils/utils";

interface DownloadModalProps {
  isOpen: boolean;
  onClose: () => void;
  files: any;
  outletName: string;
}

const DownloadModal: React.FC<DownloadModalProps> = ({ isOpen, onClose, files, outletName }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(1);

useEffect(() => {
  const currentFileType = files[currentIndex].split('.').pop()?.toLowerCase();
  if (currentFileType === 'pptx') {
    setZoomLevel(1.5); 
  } else {
    setZoomLevel(1); 
  }
}, [currentIndex, files]);

const handleNext = () => {
  setCurrentIndex((prevIndex) => {
    const newIndex = (prevIndex + 1) % files.length;
    const fileType = files[newIndex].split('.').pop()?.toLowerCase();
    if (fileType === 'pptx') {
      setZoomLevel(1.5);
    } else {
      setZoomLevel(1); 
    }
    return newIndex;
  });
};

const handlePrevious = () => {
  setCurrentIndex((prevIndex) => {
    const newIndex = (prevIndex - 1 + files.length) % files.length;
    const fileType = files[newIndex].split('.').pop()?.toLowerCase();
    if (fileType === 'pptx') {
      setZoomLevel(1.5); 
    } else {
      setZoomLevel(1); 
    }
    return newIndex;
  });
};

  const handleZoomIn = () => {
    setZoomLevel((prevZoom) => Math.min(prevZoom + 0.1, 3));
  };

  const handleZoomOut = () => {
    setZoomLevel((prevZoom) => Math.max(prevZoom - 0.1, 0.5));
  };

  const handleDownloadCurrent = async () => {
    const file = files[currentIndex];
    const fileName = file.split('/').pop() ?? 'file';
    await downloadFile(file, fileName);
  };

  const handleDownloadAll = async () => {
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fileName = file.split('/').pop() ?? 'file';
      await downloadFile(file, fileName);
    }
  };

  const currentFile = {
    uri: files[currentIndex],
    fileType: files[currentIndex].split('.').pop() ?? 'unknown',
  };

  return (
    <Modal open={isOpen} onClose={onClose} onWheel={(e) => e.stopPropagation()}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          right: '20px',
          transform: 'translateY(-50%)',
          width: '42%',
          height: '95%',
          bgcolor: 'background.paper',
          borderRadius: '8px',
          boxShadow: 24,
          p: 3,
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            left: '-20px',
            bgcolor: '#00C6B1',
            color: '#fff',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            '&:hover': {
              bgcolor: '#00C6B1',
            },
          }}
        >
          <ChevronRight style={{ width: '30px', height: '30px' }} />
        </IconButton>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
            paddingLeft: '20px',
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: '#202020',
              fontWeight: 600,
              fontSize: '15px',
            }}
          >
            {outletName}
          </Typography>

          <Typography
            onClick={handleDownloadAll}
            sx={{
              color: '#00C6B1',
              textDecoration: 'underline',
              fontSize: '15px',
              fontWeight: 600,
              cursor: 'pointer',
              '&:hover': {
                color: '#009688',
              },
            }}
          >
            Download All
          </Typography>
        </Box>

        <Box
          sx={{
            bgcolor: '#4B5363',
            borderRadius: '6px',
            p: 2,
            width: '90%',
            height: '94%',
            margin: '0 auto',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <IconButton onClick={handlePrevious}>
              <ChevronRight style={{ transform: 'rotate(180deg)', color: '#fff' }} />
            </IconButton>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                bgcolor: 'rgba(255, 255, 255, 0.3)',
                borderRadius: '4px',
                padding: '6px',
                height: '40px',
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  color: '#fff',
                  fontSize: '14px',
                  paddingLeft: '4px',
                  paddingRight: '8px'
                }}
              >
                Attachments {currentIndex + 1}/{files.length}
              </Typography>
              <Box
                sx={{
                  height: '24px',
                  borderRight: '1px solid rgba(255, 255, 255, 0.5)',
                }}
              />
              <IconButton onClick={handleZoomOut}>
                <Remove style={{ color: '#fff', fontSize: '18px' }} />
              </IconButton>
              <IconButton onClick={handleZoomIn}>
                <Add style={{ color: '#fff', fontSize: '18px' }} />
              </IconButton>

              <Box
                sx={{
                  height: '24px',
                  borderRight: '1px solid rgba(255, 255, 255, 0.5)',
                }}
              />

              <IconButton onClick={handleDownloadCurrent}>
                <DownloadOutlined style={{ color: '#fff', fontSize: '18px' }} />
              </IconButton>
            </Box>

            <IconButton onClick={handleNext}>
              <ChevronRight style={{ color: '#fff' }} />
            </IconButton>
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: 'calc(100% - 64px)',
              overflow: currentFile.fileType === 'pdf' ? 'auto' : 'hidden',
              position: 'relative',
              backgroundColor: 'transparent',
            }}
          >
            <Box
              sx={{
                transform: `scale(${zoomLevel})`,
                transformOrigin: 'center center',
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              {currentFile.fileType === 'pptx' ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'center' }}>
                  <iframe
                    src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(currentFile.uri)}`}
                    width="100%"
                    height="auto"
                    sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
                    allowFullScreen
                  />
                  <a
                    href={`https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(currentFile.uri)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      fontSize: '12px',
                      color: '#00C6B1'
                    }}
                  >
                    Open PPT In New Tab
                  </a>
                </div>
              ): (
                <DocViewer
                  key={`${currentIndex}-${zoomLevel}`}
                  documents={[currentFile]}
                  pluginRenderers={DocViewerRenderers}
                  config={{
                    header: {
                      disableHeader: true,
                      disableFileName: false,
                      retainURLParams: false,
                    },
                  }}
                  style={{
                    width: '100%',
                    height: currentFile.fileType === 'pdf' ? '100%' : 'auto',
                    backgroundColor: 'none',
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default DownloadModal;